import { Candidate } from '../Model/Candidate';
import { JobCenterModelInterface } from '../Model/JobCenterModelInterface';
import { JobVacancy } from '../Model/JobVacancy';
import { FilterSettings } from './FilterSettings';

const logDebugInformation: boolean = true;
const logColorOk = 'color: green';
const logColorErr = 'color: red';

export class Filter {
    /**
     * Filter the Models in the collection according to the given FilterSettings
     * @param {T[]} collection
     * @param {FilterSettings} settings
     * @return {T[]}
     */
    public filter<T extends JobCenterModelInterface>(collection: T[], settings: FilterSettings): T[] {
        return Array.prototype.filter.call(collection, model => this.testFilter(model, settings));
    }

    private testFilter<T extends JobCenterModelInterface>(model: T, settings: FilterSettings): boolean {
        if (settings.searchTerm && !this.testSearchTerm(model, settings.searchTerm)) {
            return false;
        }

        if (settings.industryId && !this.testIndustry(model, settings.industryId)) {
            return false;
        }

        if (settings.branchId && !this.testBranchId(model, settings.branchId)) {
            return false;
        }

        // noinspection RedundantIfStatementJS
        if (settings.sectorId && model instanceof JobVacancy && !this.testSector(model, settings.sectorId)) {
            return false;
        }

        return true;
    }

    private testSector<T extends JobVacancy>(model: T, sectorId: string | number): boolean {
        const modelSector = model.sector;
        const actualReferenceId = modelSector ? modelSector.referenceId : undefined;
        const result = actualReferenceId === '' + sectorId;

        if (logDebugInformation) {
            if (result) {
                console.debug(
                    `[Filter] %cModel's sector ${actualReferenceId} does match expected sector ${sectorId}`,
                    logColorOk
                );
            } else {
                console.debug(
                    `[Filter] %cModel's sector ${actualReferenceId} does not match expected sector ${sectorId}`,
                    logColorErr
                );
            }
        }

        return result;
    }

    private testIndustry<T extends JobCenterModelInterface>(model: T, industryId: string | number): boolean {
        const modelIndustry = model.industry;
        const actualReferenceId = modelIndustry ? modelIndustry.referenceId : undefined;
        const result = actualReferenceId === '' + industryId;

        if (logDebugInformation) {
            if (result) {
                console.debug(
                    `[Filter] %cModel's industry ${actualReferenceId} does match expected industry ${industryId}`,
                    logColorOk
                );
            } else {
                console.debug(
                    `[Filter] %cModel's industry ${actualReferenceId} does not match expected industry ${industryId}`,
                    logColorOk
                );
            }
        }

        return result;
    }

    private testBranchId<T extends JobCenterModelInterface>(model: T, branchId: number | string) {
        if (model instanceof JobVacancy) {
            const city = model.applicationContact?.city;
            const result = city === branchId;

            if (logDebugInformation) {
                if (result) {
                    console.debug(
                        `[Filter] %cModel's Application Contact city "${city}" does match expected branch "${branchId}"`,
                        logColorOk
                    );
                } else {
                    console.debug(
                        `[Filter] %cModel's Application Contact city "${city}" does not match expected branch "${branchId}"`,
                        logColorOk
                    );
                }
            }

            return result;
        } else {
            return false;
        }
    }

    private testSearchTerm<T extends JobCenterModelInterface>(model: T, searchTerm: string): boolean {
        const result = this.testModelPropertiesForSearchTerm(searchTerm, model);

        if (logDebugInformation) {
            if (result) {
                console.debug(`[Filter] %cModel does match expected search term ${searchTerm}`, logColorOk);
            } else {
                console.debug(`[Filter] %cModel does not match expected search term ${searchTerm}`, logColorErr);
            }
        }

        return result;
    }

    private testModelPropertiesForSearchTerm<T extends JobCenterModelInterface>(searchTerm: string, model: T) {
        const regex = new RegExp(searchTerm, 'i');
        if (regex.test(model.title)) {
            return true;
        }
        if (regex.test(model.additionalInformation)) {
            return true;
        }

        if (model instanceof JobVacancy) {
            return regex.test(model.description)
                || regex.test(model.requirements)
                || regex.test(model.employmentType)
                || regex.test(model.location)
                || regex.test(model.requiredLanguages);
        }
        if (model instanceof Candidate) {
            return ('' + model.yearOfBirth) === (searchTerm)
                || regex.test(model.education)
                || regex.test(model.careerAspiration)
                || regex.test(model.languages);
        }
        return false;
    }
}
